import { AgGridAngular } from "@ag-grid-community/angular";
import {
  ColDef,
  GridOptions,
  ICellRendererParams,
  ValueFormatterParams,
  ValueGetterParams,
} from "@ag-grid-community/core";
import { Component, computed, inject, linkedSignal, signal, ViewChild, ChangeDetectionStrategy } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { AccessLevel, AuthorizedAccess, OrganizationUser, UserApi } from "@front/m19-api-client";
import { OrganizationAccountGroups } from "@front/m19-models";
import {
  AuthService,
  BillingService,
  OrganizationAccountGroupService,
  OrganizationUsersService,
} from "@front/m19-services";
import {
  ConfirmPopupComponent,
  ConfirmPopupData,
  DropdownItem,
  IButtonDirective,
  IDropdownMenuComponent,
  IDropdownMenuItemDirective,
  IDropdownMenuSeparatorComponent,
  IDropdownMenuTriggerDirective,
  IGridBadgeComponent,
  IIconComponent,
  ISelectMenuImports,
  ITab,
  ModalService,
  ToasterService,
} from "@front/m19-ui";
import { catchAjaxError } from "@front/m19-utils";
import { TranslocoDirective, TranslocoService } from "@jsverse/transloco";
import { UserActivitiesGridComponent } from "@m19-board/settings/user-management/user-activities-grid/user-activities-grid.component";
import { UntilDestroy } from "@ngneat/until-destroy";
import { filter, switchMap, tap } from "rxjs";
import { GridDropdownComponent } from "../../../../../../libs/m19-ui/src/ui-components/grid-dropdown/grid-dropdown.component";
import { ITabsComponent } from "../../../../../../libs/m19-ui/src/ui-components/itabs/itabs.component";
import {
  AddAdminModalComponent,
  AddAdminModalData,
} from "./organization-card/add-admin-modal/add-admin-modal.component";
import {
  ManageAuthorizedAccessModalComponent,
  ManageAuthorizedAccessModalData,
  UserAccessMode,
} from "./organization-card/manage-authorized-user-modal/manage-authorized-access-modal.component";
import { SelectOrganizationModalComponent, SelectOrganizationModalData } from "./select-organization-modal.component";

enum UserManagementTab {
  USERS = "users",
  ACTIVITIES = "activities",
}

export enum UserRole {
  ADMIN = "Admin",
  OWNER = "Owner",
  USER = "User",
}

export type GridData = OrganizationUser & AuthorizedAccess;

export interface UserManagementData extends GridData {
  gridSection?: "admins" | "authorizedUsers";
  organizationName?: string;
  role?: UserRole;
  access?: number;
  isPending?: boolean;
}

@UntilDestroy()
@Component({
  selector: "app-user-management",
  templateUrl: "./user-management.component.html",
  imports: [
    TranslocoDirective,
    AgGridAngular,
    UserActivitiesGridComponent,
    IButtonDirective,
    IDropdownMenuComponent,
    IDropdownMenuItemDirective,
    IDropdownMenuTriggerDirective,
    ITabsComponent,
    ISelectMenuImports,
    IDropdownMenuSeparatorComponent,
    IIconComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserManagementComponent {
  private readonly translocoService = inject(TranslocoService);
  private readonly authService = inject(AuthService);
  private readonly accountGroupService = inject(OrganizationAccountGroupService);
  private readonly organizationUsersService = inject(OrganizationUsersService);
  private readonly billingService = inject(BillingService);
  private readonly toasterService = inject(ToasterService);
  private readonly modalService = inject(ModalService);
  private readonly userApi = inject(UserApi);

  readonly AccessLevel = AccessLevel;
  readonly UserManagementTab = UserManagementTab;

  selectedTab = signal<UserManagementTab>(UserManagementTab.USERS);
  readonly tabs: ITab<UserManagementTab>[] = [
    {
      label: this.translocoService.translate("common.users"),
      icon: "users",
      value: UserManagementTab.USERS,
    },
    {
      label: this.translocoService.translate("page-title.activities"),
      icon: "activity",
      value: UserManagementTab.ACTIVITIES,
    },
  ];

  user = toSignal(this.authService.loggedUser$);
  board = computed(() => this.user()?.board);
  allOrganizations = toSignal(this.accountGroupService.allOrganizationAccountGroups$);
  userOrganizations = toSignal(this.organizationUsersService.listOrganizations());
  authorizedAccess = toSignal(this.organizationUsersService.listAuthorizedAccess());
  organizationsWithBillingAccess = toSignal(this.billingService.organizationsWithBillingAccess$, {
    initialValue: [] as number[],
  });

  searchQuery = signal<string>("");
  filteredOrganizations = computed(() =>
    this.allOrganizations()?.filter((org) =>
      org.organizationName.toLowerCase().includes(this.searchQuery().toLowerCase()),
    ),
  );
  selectedOrganizations = linkedSignal(() => [this.allOrganizations()?.[0]]);

  organizationById = computed<Map<number, OrganizationAccountGroups>>(() => {
    const map = new Map<number, OrganizationAccountGroups>();
    this.allOrganizations()?.forEach((org) => {
      map.set(org.id, org);
    });
    return map;
  });

  private organizationOwnerAndAdmins = computed<UserManagementData[]>(() => {
    const data: UserManagementData[] = [];
    this.userOrganizations()?.forEach((user) => {
      const org = this.allOrganizations()?.find((org) => org.id === user.organizationId);
      if (!org) return;

      data.push({
        ...user,
        organizationName: org.organizationName,
        role: user.m19UserId === user.ownerId ? UserRole.OWNER : UserRole.ADMIN,
        isPending: !!user.invitedBy && !user.registered,
      });
    });
    return data;
  });

  private organizationAuthorizedUsers = computed<UserManagementData[]>(() => {
    const data: UserManagementData[] = [];
    const orgUser = new Set<string>();
    this.userOrganizations()?.forEach((user) => {
      const org = this.allOrganizations()?.find((org) => org.id === user.organizationId);
      if (!org) return;
      this.authorizedAccess()?.forEach((access) => {
        if (access.organizationId === org.id) {
          if (orgUser.has(access.organizationId + "_" + access.m19UserId)) return;
          data.push({
            ...access,
            organizationName: org.organizationName,
            role: UserRole.USER,
            isPending: !!access.invitedBy && !access.registered,
          });
          orgUser.add(access.organizationId + "_" + access.m19UserId);
        }
      });
    });
    return data;
  });

  gridData = computed<UserManagementData[]>(() => {
    const selectedOrganizations = this.selectedOrganizations();
    const data = [...this.organizationOwnerAndAdmins(), ...this.organizationAuthorizedUsers()];
    return data.filter((user) => selectedOrganizations.some((org) => org?.id === user.organizationId));
  });

  private organizationAccessByUserId = computed<Map<number, AuthorizedAccess[]>>(() => {
    const map = new Map<number, AuthorizedAccess[]>();
    this.authorizedAccess()?.forEach((access) => {
      map.set(access.m19UserId!, [...(map.get(access.m19UserId!) ?? []), access]);
    });
    return map;
  });

  private userAccessEmails = computed<string[]>(() => {
    return Array.from(this.organizationAccessByUserId().values())
      .flatMap((access) => access.map((a) => a.email))
      .filter((email): email is string => email !== undefined);
  });

  private adminsEmails = computed<string[]>(() => {
    return this.organizationOwnerAndAdmins()
      .map((user) => user.email)
      .filter((email): email is string => email !== undefined);
  });

  @ViewChild(AgGridAngular) agGrid!: AgGridAngular;

  private defaultColDef: ColDef = {
    sortable: true,
    filter: true,
    floatingFilter: true,
    suppressMovable: true,
    cellClass: "flex items-center",
  };

  private columnDefs: ColDef<UserManagementData>[] = [
    {
      field: "organizationId",
      valueGetter: (params: ValueGetterParams<UserManagementData>) => {
        return params.data?.organizationName;
      },
      headerName: this.translocoService.translate("account-setting.organization"),
      filter: "agTextColumnFilter",
    },
    {
      flex: 1,
      headerName: this.translocoService.translate("common.name"),
      filter: "agTextColumnFilter",
      valueGetter: (params: ValueGetterParams<UserManagementData>) => {
        const firstName = params.data?.firstName;
        const lastName = params.data?.lastName;
        return firstName && lastName ? `${firstName} ${lastName}` : params.data?.userName;
      },
    },
    {
      field: "email",
      flex: 1,
      headerName: this.translocoService.translate("manage-authorized-access-modal.email"),
      filter: "agTextColumnFilter",
      cellClass: "text-gray-600 font-medium",
    },
    {
      field: "invitedBy",
      headerName: "Status",
      width: 120,
      valueGetter: (params: ValueGetterParams<UserManagementData>) => {
        return this.translocoService
          .translate(params.data?.isPending ? "user-management.pending" : "user-management.accepted")
          .toUpperCase();
      },
      valueFormatter: (_) => {
        return ""; // hide value if user is not pending
      },
      filter: "agSetColumnFilter",
      cellRendererSelector: (params: ICellRendererParams<UserManagementData>) => {
        if (params.data?.isPending) {
          const lastInviteDate = new Date(params.data.lastInvite!);
          const lastInviteDateString = lastInviteDate.toLocaleDateString(this.user()?.locale ?? "en-US", {
            year: "numeric",
            month: "long",
            day: "numeric",
          });
          return {
            component: IGridBadgeComponent,
            params: {
              icon: "mail",
              label: this.translocoService.translate("user-management.pending").toUpperCase(),
              color: "orange",
              size: "sm",
              variant: "soft",
              tooltipValue: this.translocoService.translate("user-management.last_invite_sent", [lastInviteDateString]),
            },
          };
        }
        return undefined;
      },
    },
    {
      field: "role",
      headerName: this.translocoService.translate("user-management.role"),
      width: 120,
      filter: "agSetColumnFilter",
      filterParams: {
        values: [UserRole.ADMIN, UserRole.OWNER, UserRole.USER],
      },
      cellClass: "text-gray-600 font-medium",
    },
    {
      field: "creationTime",
      headerName: "Joined On",
      width: 120,
      filter: "agDateColumnFilter",
      valueFormatter: (params: ValueFormatterParams<UserManagementData>) => {
        return params.value ? new Date(params.value).toLocaleDateString() : "-";
      },
    },
    {
      colId: "actions",
      flex: 1,
      headerName: "",
      filter: false,
      floatingFilter: false,
      cellClass: "flex items-center justify-end",
      cellRendererSelector: (params: ICellRendererParams<UserManagementData>) => {
        const data = params.node?.data;
        if (!data) return undefined;
        const userData = data;
        const selectedOrganization = this.organizationById().get(data.organizationId!);
        const items: DropdownItem[][] = [[]];
        if (userData?.isPending) {
          items[0].push({
            icon: "mail",
            label: this.translocoService.translate("user-management.resend_invite"),
            onClick: () => this.resendInvite(userData),
          });
        }

        if (userData?.role === UserRole.USER) {
          items[0].push({
            icon: "gear",
            label: this.translocoService.translate("account-setting.manage_access"),
            onClick: () => this.openManageAuthorizedUserModal(userData),
          });
          items[0].push({
            icon: "close",
            label: this.translocoService.translate("user-management.revoke"),
            onClick: () => this.revokeAuthorizedAccess(userData),
            variant: "danger",
          });
        }

        if (
          userData?.role === UserRole.ADMIN &&
          userData?.ownerId !== userData?.m19UserId &&
          userData?.m19UserId !== this.user()?.userId
        ) {
          if (this.canManageBillingAccess(userData.organizationId!)) {
            items[0].push({
              icon: "creditCard",
              label: this.translocoService.translate(
                userData.billingAccess
                  ? "user-management.revoke_billing_access"
                  : "user-management.give_billing_access",
              ),
              onClick: () => this.toggleBillingAccess(userData),
              variant: userData.billingAccess ? "danger" : undefined,
            });
          }
          items[0].push({
            icon: "close",
            label: this.translocoService.translate("user-management.revoke"),
            onClick: () => this.revokeAdminAccess(userData),
            variant: "danger",
          });
        }

        const hasSubscription = !!selectedOrganization?.hasSubscription();
        return items[0].length > 0
          ? {
              component: GridDropdownComponent,
              params: {
                icon: "moreHorizontal",
                items: items,
                disabled: !hasSubscription,
                tooltip: hasSubscription
                  ? undefined
                  : this.translocoService.translate("user-management.no_subscription_tooltip"),
              },
            }
          : undefined;
      },
    },
  ];

  gridOptions: GridOptions<UserManagementData> = {
    defaultColDef: this.defaultColDef,
    suppressRowHoverHighlight: true,
    suppressMultiRangeSelection: true,
    sideBar: false,
    columnDefs: this.columnDefs,
    enableRangeSelection: false,
    getRowId: (params) => params.data.organizationId + "_" + params.data.m19UserId + "_" + params.data.role,
  };

  constructor() {
    // old UI
    localStorage.removeItem("user-management-grid_gridConfig");
    localStorage.removeItem("userManagementGrid_gridConfig");
  }

  private canManageBillingAccess(organizationId: number): boolean {
    const org = this.organizationById().get(organizationId);
    if (!org) return false;
    if (org.organization.ownerId === this.user()?.userId) return true;
    return this.organizationsWithBillingAccess().includes(organizationId);
  }

  private toggleBillingAccess(admin: UserManagementData): void {
    const org = this.organizationById().get(admin.organizationId!);
    if (!org) return;

    const isGranting = !admin.billingAccess;
    const titleKey = isGranting ? "user-management.give_billing_access" : "user-management.revoke_billing_access";
    const messageKey = isGranting
      ? "user-management.give_billing_access_confirmation"
      : "user-management.revoke_billing_access_confirmation";

    const ref = this.modalService.openModal<ConfirmPopupData, boolean>(ConfirmPopupComponent, {
      modalTitle: this.translocoService.translate(titleKey),
      data: {
        message: this.translocoService.translate(messageKey, [admin.userName ?? admin.email, org.organizationName]),
      },
    });

    ref
      .afterClosed()
      .pipe(
        filter((result) => result === true),
        tap(() => this.toggleGridLoading(true)),
        switchMap(() => this.organizationUsersService.toggleBillingAccess(admin.organizationId!, admin.m19UserId!)),
      )
      .subscribe({
        next: () => {
          const successKey = isGranting
            ? "user-management.give_billing_access_success"
            : "user-management.revoke_billing_access_success";
          this.toasterService.success(this.translocoService.translate(successKey, [admin.email, org.organizationName]));
          this.toggleGridLoading(false);
        },
        error: (error) => {
          this.toasterService.error(error?.response?.message ?? error);
          this.toggleGridLoading(false);
        },
      });
  }

  private revokeAdminAccess(admin: UserManagementData): void {
    const org = this.organizationById().get(admin.organizationId!);
    if (!org) return;

    const message = admin.isPending
      ? this.translocoService.translate("user-management.cancel_invite_confirmation", [admin.email])
      : this.translocoService.translate("user-management.revoke_admin_confirmation", [
          admin.userName ?? "",
          admin.email,
          org.organizationName,
        ]);
    const ref = this.modalService.openModal<ConfirmPopupData, boolean>(ConfirmPopupComponent, {
      modalTitle: this.translocoService.translate("user-management.revoke"),
      data: {
        message,
      },
    });

    ref
      .afterClosed()
      .pipe(
        filter((result) => result === true),
        tap(() => this.toggleGridLoading(true)),
        switchMap(() => this.organizationUsersService.revokeAdminAccessV2(admin.organizationId!, admin.m19UserId!)),
      )
      .subscribe({
        next: () => {
          const message = admin.isPending
            ? this.translocoService.translate("user-management.cancel_invite_success", [admin.email])
            : this.translocoService.translate("user-management.revoke_admin_success", [
                admin.email,
                org.organizationName,
              ]);
          this.toasterService.success(message);
          this.toggleGridLoading(false);
        },
        error: (error) => {
          this.toasterService.error(error.response.message);
          this.toggleGridLoading(false);
        },
      });
  }

  openManageAuthorizedUserModal(user: UserManagementData): void {
    const org = this.organizationById().get(user.organizationId!);

    if (!org || !org.hasActiveSubscription()) return;

    const email = user.email;
    const userName = user.userName;
    const authorizedAccess = new Map(
      this.organizationAccessByUserId()
        .get(user.m19UserId!)!
        .map((x) => [x.profileId, x]),
    );
    const data: ManageAuthorizedAccessModalData = {
      accessMode: UserAccessMode.UPDATE_ACCESS,
      userEmail: email,
      userName: userName!,
      board: this.board()!,
      initialAuthorizedOrganizationProfileIds: authorizedAccess,
      organizationId: org.id,
      organizationName: org.organizationName,
    };
    this.modalService.openModal<ManageAuthorizedAccessModalData, void>(ManageAuthorizedAccessModalComponent, {
      size: "xl",
      height: "fill",
      modalTitle: this.translocoService.translate("manage-authorized-access-modal.authorized_user", {
        name: org.organizationName,
        new: false,
      }),
      data,
    });
  }

  private revokeAuthorizedAccess(user: UserManagementData): void {
    const email = user.email;
    const org = this.organizationById().get(user.organizationId!);
    const access = this.organizationAccessByUserId().get(user.m19UserId!)!;

    const message = this.translocoService.translate("user-management.revoke_user_confirmation", [
      user.userName ?? "",
      user.email,
      org?.organizationName,
    ]);
    const ref = this.modalService.openModal<ConfirmPopupData, boolean>(ConfirmPopupComponent, {
      modalTitle: this.translocoService.translate("user-management.revoke"),
      data: {
        message,
      },
    });

    ref
      .afterClosed()
      .pipe(
        filter((result) => result === true),
        tap(() => this.toggleGridLoading(true)),
        switchMap(() => this.organizationUsersService.revokeAuthorizedAccessAsync(access)),
      )
      .subscribe({
        next: () => {
          this.toasterService.success(
            "Successfully revoked access from " + email + " to " + access.length + " accounts",
            "Admin access revoked",
          );
          this.toggleGridLoading(false);
        },
        error: (error) => {
          this.toasterService.error(error, "Admin access revocation error");
          this.toggleGridLoading(false);
        },
      });
  }

  openAddAdminModal(): void {
    this.modalService.openModal<AddAdminModalData, void>(AddAdminModalComponent, {
      size: "sm",
      modalTitle: this.translocoService.translate("add-admin-modal.add_admin"),
      data: {
        allOrganizations: this.allOrganizations()!,
      },
    });
  }

  openAddNewAuthorizedUserModal(): void {
    const ref = this.modalService.openModal<SelectOrganizationModalData, OrganizationAccountGroups>(
      SelectOrganizationModalComponent,
      {
        size: "sm",
        modalTitle: this.translocoService.translate("data-sharing.select_an_organization"),
        data: {
          allOrganizations: this.allOrganizations()!,
        },
      },
    );

    ref.afterClosed().subscribe((org: OrganizationAccountGroups) => {
      if (!org || !org.hasActiveSubscription()) return;
      const data: ManageAuthorizedAccessModalData = {
        accessMode: UserAccessMode.ADD_ACCESS,
        board: this.board()!,
        organizationId: org.id,
        organizationName: org.organizationName,
        adminsEmails: this.adminsEmails(),
        authorizedUserEmails: this.userAccessEmails(),
      };

      this.modalService.openModal<ManageAuthorizedAccessModalData, void>(ManageAuthorizedAccessModalComponent, {
        size: "xl",
        height: "fill",
        modalTitle: this.translocoService.translate("manage-authorized-access-modal.authorized_user", {
          name: org.organizationName,
          new: true,
        }),
        data,
      });
    });
  }

  private toggleGridLoading(loading: boolean): void {
    if (loading) {
      this.agGrid.api.showLoadingOverlay();
    } else {
      this.agGrid.api.hideOverlay();
    }
  }

  private resendInvite(user: UserManagementData): void {
    const org = this.organizationById().get(user.organizationId!);
    if (!user.isPending || !user.email || !org?.id) return;

    this.toggleGridLoading(true);
    this.userApi
      .sendUserInvitation({
        email: user.email,
        domain: window.location.hostname,
        organizationId: org!.id!,
      })
      .pipe(catchAjaxError())
      .subscribe({
        next: () => {
          this.toasterService.success(this.translocoService.translate("user-management.invite_resent", [user.email]));
          this.toggleGridLoading(false);
        },
        error: (error) => {
          this.toasterService.error(error, "Invite resend error");
          this.toggleGridLoading(false);
        },
      });
  }
}
